import { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Wrench, IdentificationCard, ImageSquare, Package, ShieldCheck, ArrowsClockwise, FloppyDisk } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function PanValidator() {
  const [pan, setPan] = useState('');
  const [result, setResult] = useState(null);
  const validate = async () => {
    try {
      const { data } = await axios.post(`${API}/api/seller/validate-pan`, { pan }, { withCredentials: true });
      setResult(data);
    } catch { toast.error('Validation failed'); }
  };
  return (
    <div className="bg-white rounded-xl border border-[#e5e5ea] p-5" data-testid="pan-validator">
      <div className="flex items-center gap-2 mb-3">
        <IdentificationCard size={20} className="text-[#7d64ff]" />
        <h3 className="font-semibold text-[#0A0A0A]">PAN Card Validation</h3>
      </div>
      <div className="flex gap-2">
        <Input data-testid="pan-input" value={pan} onChange={e => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" className="max-w-[200px] font-mono" maxLength={10} />
        <Button onClick={validate} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="validate-pan-btn">Validate</Button>
      </div>
      {result && (
        <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${result.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="pan-result">
          {result.valid ? `Valid ${result.type} PAN: ${result.pan}` : `Invalid PAN format: ${result.pan}`}
        </div>
      )}
    </div>
  );
}

function ImageValidator() {
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setValidating(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/products/validate-image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
    } catch { toast.error('Validation failed'); }
    finally { setValidating(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-[#e5e5ea] p-5" data-testid="image-validator">
      <div className="flex items-center gap-2 mb-3">
        <ImageSquare size={20} className="text-[#7d64ff]" />
        <h3 className="font-semibold text-[#0A0A0A]">Product Image Validation</h3>
      </div>
      <p className="text-xs text-[#6b6b8a] mb-3">Check if image meets marketplace requirements (JPEG/PNG/WebP, max 5MB)</p>
      <input type="file" accept="image/*" onChange={handleFile} className="text-sm" data-testid="image-file-input" />
      {validating && <p className="text-sm text-[#9c9db7] mt-2">Validating...</p>}
      {result && (
        <div className={`mt-3 px-3 py-2 rounded-lg text-sm ${result.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="image-result">
          <p className="font-medium">{result.valid ? 'Image is valid' : 'Image validation failed'}</p>
          <p className="text-xs mt-1">{result.file_name} ({(result.file_size / 1024).toFixed(1)}KB) &mdash; {result.content_type}</p>
          {result.errors?.length > 0 && <ul className="mt-1 text-xs list-disc list-inside">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}

function NorRrfConfig() {
  const [config, setConfig] = useState({ return_net_off: true, rrf_enabled: true, rrf_deduction_percent: 5.0, auto_adjust_settlement: true });
  const [loaded, setLoaded] = useState(false);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/nor-rrf-config`, { withCredentials: true });
      setConfig(c => ({ ...c, ...data }));
      setLoaded(true);
    } catch { setLoaded(true); }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/api/seller/nor-rrf-config`, config, { withCredentials: true });
      toast.success('NOR/RRF config saved');
    } catch { toast.error('Failed to save'); }
  };

  if (!loaded) { fetchConfig(); }

  return (
    <div className="bg-white rounded-xl border border-[#e5e5ea] p-5" data-testid="nor-rrf-config">
      <div className="flex items-center gap-2 mb-3">
        <ArrowsClockwise size={20} className="text-[#7d64ff]" />
        <h3 className="font-semibold text-[#0A0A0A]">NOR & RRF Configuration</h3>
      </div>
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={config.return_net_off} onChange={e => setConfig(c => ({ ...c, return_net_off: e.target.checked }))} className="rounded" /> Enable Net-Off Returns (NOR)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={config.rrf_enabled} onChange={e => setConfig(c => ({ ...c, rrf_enabled: e.target.checked }))} className="rounded" /> Enable Return Restocking Fee (RRF)</label>
        {config.rrf_enabled && (
          <div><Label>RRF Deduction %</Label><Input data-testid="rrf-percent" type="number" step="0.5" value={config.rrf_deduction_percent} onChange={e => setConfig(c => ({ ...c, rrf_deduction_percent: parseFloat(e.target.value) || 5 }))} className="max-w-[120px]" /></div>
        )}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={config.auto_adjust_settlement} onChange={e => setConfig(c => ({ ...c, auto_adjust_settlement: e.target.checked }))} className="rounded" /> Auto-adjust settlements</label>
        <Button onClick={handleSave} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="save-nor-rrf-btn"><FloppyDisk size={14} className="mr-1" /> Save</Button>
      </div>
    </div>
  );
}

function StoreCapacity() {
  const [form, setForm] = useState({ store_id: '', date: '', max_orders: 100, max_items: 500 });
  const handleSave = async () => {
    if (!form.store_id || !form.date) { toast.error('Store ID and date required'); return; }
    try {
      await axios.post(`${API}/api/seller/store-capacity`, form, { withCredentials: true });
      toast.success('Capacity set');
    } catch { toast.error('Failed to save'); }
  };
  return (
    <div className="bg-white rounded-xl border border-[#e5e5ea] p-5" data-testid="store-capacity">
      <div className="flex items-center gap-2 mb-3">
        <Package size={20} className="text-[#7d64ff]" />
        <h3 className="font-semibold text-[#0A0A0A]">Store Capacity Planning</h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Store ID</Label><Input value={form.store_id} onChange={e => setForm(f => ({ ...f, store_id: e.target.value }))} placeholder="store_..." /></div>
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
        <div><Label>Max Orders</Label><Input type="number" value={form.max_orders} onChange={e => setForm(f => ({ ...f, max_orders: parseInt(e.target.value) || 100 }))} /></div>
        <div><Label>Max Items</Label><Input type="number" value={form.max_items} onChange={e => setForm(f => ({ ...f, max_items: parseInt(e.target.value) || 500 }))} /></div>
      </div>
      <Button onClick={handleSave} className="bg-[#7d64ff] hover:bg-[#6b4fef] mt-3" data-testid="save-capacity-btn"><FloppyDisk size={14} className="mr-1" /> Set Capacity</Button>
    </div>
  );
}

export default function SellerToolsPage() {
  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl" data-testid="seller-tools-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Seller Tools</h1>
          <p className="text-sm text-[#6b6b8a] mt-1">Utilities for validation, capacity planning, and return configuration</p>
        </div>
        <div className="space-y-4">
          <PanValidator />
          <ImageValidator />
          <StoreCapacity />
          <NorRrfConfig />
        </div>
      </div>
    </DashboardLayout>
  );
}
